import { untrack } from 'svelte'
import { SvelteSet } from 'svelte/reactivity'
import { onPlayPauseShortcut } from '../util/play_pause_shortcut'
import type PlayerClass from 'player/javascripts/player'

type PlayerInstance = InstanceType<typeof PlayerClass>

export interface SlotGeometry {
  left: number
  width: number
  height: number
}

export interface SlotHandle {
  setGeometry(geometry: SlotGeometry): void
  release(): void
}

export interface RegisteredPlayer {
  readonly container: HTMLElement
  readonly offScreen: boolean
  readonly playing: boolean
  /** While true the play/pause shortcut leaves this player alone, e.g. during the pre-ad screen. */
  readonly playbackLocked?: boolean
  readonly player: Promise<PlayerInstance>
  pause(): void
  togglePlayback(): void
  refreshVisibility(): void
}

class Players {
  #all = new SvelteSet<RegisteredPlayer>()

  #dockOwner = $state.raw<RegisteredPlayer | undefined>()
  #dockOwnerNow: RegisteredPlayer | undefined

  #slotCount = 0
  #hasSlot = $state(false)

  #geometry = $state.raw<SlotGeometry | undefined>()
  #geometryOwner: SlotHandle | undefined

  #suspensions = 0
  #suspended = $state(false)
  #frozen = $state.raw<RegisteredPlayer | undefined>()

  #releaseShortcut: (() => void) | undefined

  register(player: RegisteredPlayer): () => void {
    this.#all.add(player)
    this.#releaseShortcut ??= onPlayPauseShortcut(() => this.togglePlayback())

    return () => {
      this.#all.delete(player)
      this.#releaseDock(player)
      if (this.#all.size === 0) {
        this.#releaseShortcut?.()
        this.#releaseShortcut = undefined
      }
    }
  }

  togglePlayback(): boolean {
    for (const player of this.#all) {
      if (player.playing) {
        if (!player.playbackLocked) {
          player.pause()
        }
        return true
      }
    }
    if (this.#dockOwnerNow) {
      if (!this.#dockOwnerNow.playbackLocked) {
        this.#dockOwnerNow.togglePlayback()
      }
      return true
    }
    return false
  }

  registerSlot(): SlotHandle {
    this.#slotCount += 1
    this.#hasSlot = true
    let open = true

    const handle: SlotHandle = {
      setGeometry: (geometry) => {
        this.#geometryOwner = handle
        this.#geometry = geometry
      },

      release: () => {
        if (!open) {
          return
        }
        open = false
        this.#slotCount -= 1
        this.#hasSlot = this.#slotCount > 0
        if (this.#geometryOwner === handle) {
          this.#geometryOwner = undefined
          this.#geometry = undefined
        }
      },
    }

    return handle
  }

  suspendDocking(): () => void {
    if (this.#suspensions === 0) {
      this.#frozen = untrack(() => this.docked)
      this.#suspended = true
    }
    this.#suspensions += 1

    return () => {
      this.#suspensions -= 1
      if (this.#suspensions === 0) {
        this.#suspended = false
        this.#frozen = undefined
      }
    }
  }

  refreshVisibility(): void {
    for (const player of this.#all) {
      player.refreshVisibility()
    }
  }

  claimPlayback(player: RegisteredPlayer): void {
    for (const other of this.#all) {
      if (other !== player) {
        other.pause()
      }
    }
  }

  claimDock(player: RegisteredPlayer): () => void {
    this.#dockOwnerNow = player
    this.#dockOwner = player
    return () => this.#releaseDock(player)
  }

  #releaseDock(player: RegisteredPlayer): void {
    if (this.#dockOwnerNow === player) {
      this.#dockOwnerNow = undefined
      this.#dockOwner = undefined
    }
  }

  readonly docked = $derived.by(() => {
    if (!this.#hasSlot) {
      return undefined
    }
    if (this.#suspended) {
      return this.#frozen && this.#frozen === this.#dockOwner ? this.#frozen : undefined
    }
    return this.#dockOwner?.offScreen ? this.#dockOwner : undefined
  })

  get slotGeometry(): SlotGeometry | undefined {
    return this.#geometry
  }

  observeDocked(callback: (docked: boolean) => void): () => void {
    return $effect.root(() => {
      $effect(() => {
        callback(this.docked !== undefined)
      })
    })
  }

  playerIn(element: HTMLElement): Promise<PlayerInstance> {
    const found = this.#playerInside(element)
    if (found) {
      return found.player
    }

    return new Promise<PlayerInstance>((resolve) => {
      const stop = $effect.root(() => {
        $effect(() => {
          const match = this.#playerInside(element)
          if (match) {
            resolve(match.player)
            queueMicrotask(stop)
          }
        })
      })
    })
  }

  #playerInside(element: HTMLElement): RegisteredPlayer | undefined {
    for (const player of this.#all) {
      if (element.contains(player.container)) {
        return player
      }
    }
    return undefined
  }
}

export const players = new Players()
